/** Which SDK sends an event: the web SDK alone, the Flutter package alone, or
 *  every SDK (TypeScript, Dart, Python). */
export type SystemEventEmitter = 'every' | 'flutter' | 'web';

export type SystemEvent = {
  emittedBy: SystemEventEmitter;
  meaning: string;
  name: string;
  properties: readonly string[];
};

export const SYSTEM_EVENT = {
  autocapture: '$autocapture',
  createAlias: '$create_alias',
  featureFlagCalled: '$feature_flag_called',
  group: '$group',
  identify: '$identify',
  pageleave: '$pageleave',
  pageview: '$pageview',
  screen: '$screen',
  set: '$set',
} as const;

export type SystemEventName = (typeof SYSTEM_EVENT)[keyof typeof SYSTEM_EVENT];

/** Every event name the platform owns, what each one means and what it carries.
 *  The one list: the emitters read their names from `SYSTEM_EVENT`, the docs
 *  render this table, and the console explains a `$` name from it. */
export const SYSTEM_EVENTS: readonly SystemEvent[] = [
  {
    emittedBy: 'web',
    meaning: 'A click anywhere on the page, with the element that was clicked. The web SDK sends it by default; autocapture: false or autocaptureOptions.clicks: false turns it off.',
    name: SYSTEM_EVENT.autocapture,
    properties: ['$event_type', '$el_description', '$el_description_source', '$el_action', '$el_tag', '$el_text', '$el_id', '$el_classes', '$el_href', '$current_url', '$pathname', '$host', '$title', '$referrer'],
  },
  {
    emittedBy: 'web',
    meaning: 'A page shown, including every client-side navigation the History API makes: one per distinct path and query.',
    name: SYSTEM_EVENT.pageview,
    properties: ['$current_url', '$pathname', '$host', '$title', '$referrer'],
  },
  {
    emittedBy: 'web',
    meaning: "A landing page left the visitor's sight, by a tab switch, a closed tab or a navigation away, with the seconds it was in front of them so far. A page returned to and left again sends it again with the larger total.",
    name: SYSTEM_EVENT.pageleave,
    properties: ['$engaged_seconds', '$page_id', '$page_view_id', '$page_variant'],
  },
  {
    emittedBy: 'flutter',
    meaning: 'A named route pushed or replaced in a Flutter app, the mobile counterpart of $pageview.',
    name: SYSTEM_EVENT.screen,
    properties: ['screen'],
  },
  {
    emittedBy: 'every',
    meaning: 'A person becomes known by a distinct id. The anonymous trail before it is joined to them, and any traits ride along under $set.',
    name: SYSTEM_EVENT.identify,
    properties: ['$anon_distinct_id', '$set'],
  },
  {
    emittedBy: 'every',
    meaning: 'A second id for the same person, so events sent under either resolve to one profile.',
    name: SYSTEM_EVENT.createAlias,
    properties: ['alias'],
  },
  {
    emittedBy: 'every',
    meaning: 'The person is placed in a group such as a company or a team; group properties ride along under $set.',
    name: SYSTEM_EVENT.group,
    properties: ['$group_type', '$group_key', '$set'],
  },
  {
    emittedBy: 'every',
    meaning: 'Person properties written without an identify: $set overwrites, $set_once fills only what is still empty.',
    name: SYSTEM_EVENT.set,
    properties: ['$set', '$set_once'],
  },
  {
    emittedBy: 'every',
    meaning: 'A flag was evaluated for this person, sent once per distinct answer. Experiments attribute exposure from it.',
    name: SYSTEM_EVENT.featureFlagCalled,
    properties: ['$feature_flag', '$feature_flag_response'],
  },
];

export const isSystemEventName = (name: string): boolean => name.startsWith('$');

export const systemEvent = (name: string): SystemEvent | undefined => SYSTEM_EVENTS.find(event => event.name === name);
